<template>
  <a-modal
    :open="visible"
    :title="title"
    :footer="null"
    :mask-closable="false"
    :closable="!loading"
    :keyboard="!loading"
    width="520px"
    @cancel="onCancel"
  >
    <!-- Stage 1: form -->
    <div v-if="stage === 'form'">
      <a-form layout="vertical" :disabled="loading">
        <a-form-item v-if="!isCopyMode" label="创建方式">
          <a-radio-group v-model:value="batchMode" button-style="solid">
            <a-radio-button :value="false">单个创建</a-radio-button>
            <a-radio-button :value="true">批量创建</a-radio-button>
          </a-radio-group>
        </a-form-item>
        <a-form-item label="所属用户" required>
          <ProxyUserPicker v-model="selectedProxyUserID" />
        </a-form-item>

        <a-form-item v-if="!props.region" label="大区（可多选）" :rules="[{ required: true, message: '请选择大区' }, { type: 'array', min: 1, message: '至少选 1 个大区' }]">
          <a-select
            v-model:value="selectedRegionIds"
            mode="multiple"
            placeholder="选择 1 个或多个大区"
            :options="regionOptions"
            :loading="regionsLoading"
            :max-tag-count="3"
            show-search
            :filter-option="filterRegion"
          />
        </a-form-item>

        <a-form-item v-if="isBatchMode" label="服务器（可多选）" required
          extra="每台服务器创建一个 proxy，使用各自默认端口和系统默认配置，自动生成独立账号密码。">
          <a-select v-model:value="selectedNodeIds" mode="multiple" placeholder="请选择服务器"
            :options="nodeOptions" :loading="nodesLoading" show-search :filter-option="filterNode" />
          <a-button type="link" size="small" :disabled="loading" @click="selectedNodeIds = nodeOptions.map(n => n.value)">全选可用服务器</a-button>
          <span>已选 {{ selectedNodeIds.length }} 台</span>
        </a-form-item>

        <a-form-item v-else
          label="节点"
          :required="isCopyMode"
          :extra="isCopyMode
            ? '复制创建必须手动选择目标节点。列表只显示在线、有空位且尚未占用所选大区的节点。'
            : '默认「自动分配」由 alloc 选可用节点；也可手动指定具体节点。列表只显示此大区可用节点：在线 + 节点有空位 + 该大区尚未占用。'"
        >
          <a-select
            v-model:value="selectedNodeId"
            :placeholder="isCopyMode ? '请选择目标节点' : '自动分配'"
            :options="nodeOptions"
            :loading="nodesLoading"
            allow-clear
            show-search
            :filter-option="filterNode"
          />
          <div v-if="nodeExpireConflict" style="margin-top:8px;color:#ef4444;font-size:12px">
            该服务器到期早于节点到期（服务器到期 {{ nodeExpireLabel }}，节点到期 {{ expireAt }}）。请选择更长有效期服务器或缩短节点有效期。
          </div>
        </a-form-item>

        <a-form-item v-if="!isCopyMode" label="有效期">
          <a-radio-group v-model:value="duration" button-style="solid" @change="recalc">
            <a-radio-button value="1m">1个月</a-radio-button>
            <a-radio-button value="3m">3个月</a-radio-button>
            <a-radio-button value="6m">6个月</a-radio-button>
            <a-radio-button value="12m">12个月</a-radio-button>
            <a-radio-button value="custom">自定义</a-radio-button>
          </a-radio-group>

          <div v-if="duration === 'custom'" style="margin-top:12px;display:flex;align-items:center;gap:12px;flex-wrap:wrap">
            <span>数量</span>
            <a-input-number
              v-model:value="customN"
              :min="1"
              :max="unit === 'd' ? 730 : 24"
              style="width:120px"
            />
            <a-radio-group v-model:value="unit" button-style="solid" @change="recalc">
              <a-radio-button value="d">天</a-radio-button>
              <a-radio-button value="m">月</a-radio-button>
            </a-radio-group>
            <span style="color:#6b7280">→ 到期: <span style="color:#1f2937;font-weight:500">{{ expireAt }}</span></span>
          </div>
        </a-form-item>

        <a-form-item
          v-else
          label="有效期"
          required
          extra="已复制原节点的到期日期，可直接修改。"
        >
          <a-input v-model:value="expireAt" type="date" />
        </a-form-item>

        <a-form-item v-if="!isBatchMode"
          label="端口"
          :extra="isCopyMode
            ? '选定目标节点后自动填入该节点可用端口；可手工修改，留空则由系统自动分配。'
            : '选定节点后优先填入节点预置端口，其次使用最近释放端口；可手工修改。留空时由系统自动分配。'"
        >
          <div style="display:flex;align-items:center;gap:8px">
            <a-input-number
              v-model:value="port"
              :min="1"
              :max="65535"
              placeholder="自动"
              style="width:140px"
              @change="onPortChange"
            />
            <a-spin v-if="portSuggestionLoading" size="small" />
          </div>
        </a-form-item>

        <template v-if="isCopyMode">
          <a-form-item label="账号" required extra="新账号默认生成 6 位数字，可修改。">
            <a-input
              v-model:value="username"
              :maxlength="6"
              inputmode="numeric"
              placeholder="6 位数字"
            />
          </a-form-item>

          <a-form-item label="密码" required extra="新密码默认生成 6 位数字，可修改。">
            <a-input
              v-model:value="password"
              :maxlength="6"
              inputmode="numeric"
              placeholder="6 位数字"
            />
          </a-form-item>
        </template>

        <a-form-item
          label="备注"
          extra="可选。用于记录客户/游戏账号/内部单号等。最多 512 字符。"
        >
          <a-textarea
            v-model:value="note"
            :rows="2"
            :maxlength="512"
            placeholder="例: 张三 - 网通一区主号 - 单号 2026-001"
          />
        </a-form-item>

        <a-form-item v-if="!isCopyMode" label="到期时间">
          <span style="color:#1f2937">{{ expireAt }}</span>
        </a-form-item>
      </a-form>

      <a-button
        type="primary"
        :loading="loading"
        @click="onSubmit"
        block
        :disabled="submitDisabled"
      >{{ loading && isBatchMode ? `正在创建 ${batchProgress}/${selectedNodeIds.length}` : isBatchMode ? `批量创建（${selectedNodeIds.length} 个）` : isCopyMode ? '确认复制创建' : '生成' }}</a-button>
    </div>

    <div v-else-if="stage === 'batch-done'">
      <a-result :status="batchFailures.length ? 'warning' : 'success'"
        :title="`已创建 ${batchCreated.length} 个 proxy`" sub-title="每行一个配置：IP|端口|账号|密码|到期。" />
      <a-alert v-for="failure in batchFailures" :key="failure.nodeID" type="warning" show-icon
        :message="`${failure.nodeName}：${failure.reason}`" style="margin-bottom:8px" />
      <div v-for="item in batchCreated" :key="item.proxy_id" class="batch-preview">
        <strong>{{ item.node_name }}</strong>
        <div class="batch-config">{{ item.copy_string || '地址待解析，请点击一键复制重试解析' }}</div>
      </div>
      <a-space wrap style="margin-top:16px">
        <a-button type="primary" :disabled="!batchCreated.length" :loading="batchCopying" @click="copyBatch">一键复制全部</a-button>
        <a-button :disabled="!createdProxyIds.length || loading || batchCopying" @click="openHealthcheck">批量检测</a-button>
        <a-button v-if="retryNodeIds.length" :loading="loading" @click="submitBatch(retryNodeIds)">重试失败项</a-button>
        <a-button :disabled="loading || batchCopying" @click="onAnother">继续创建</a-button>
        <a-button :disabled="loading || batchCopying" @click="onClose">关闭</a-button>
      </a-space>
    </div>

    <!-- Stage 2: success / display credentials -->
    <div v-else-if="stage === 'done' && created">
      <a-result status="success" title="已生成" sub-title="把下面 5 个字段发给玩家,过期前都可使用。">
        <template #extra>
          <a-tag color="green">大区 {{ created.region_names?.join(' + ') || effectiveRegion?.region_name }} · 节点 {{ created.node_name }}</a-tag>
        </template>
      </a-result>

      <div class="cred-block">
        <div class="cred-row">
          <span class="cred-label">IP</span>
          <span class="cred-value">{{ created.ip_public || '—' }}</span>
          <a-button v-if="created.ip_public" size="small" type="link" @click="copyField(created.ip_public, 'IP')">复制</a-button>
        </div>

        <a-divider style="margin:8px 0" />

        <div class="cred-row">
          <span class="cred-label">端口</span>
          <span class="cred-value">{{ created.port }}</span>
          <a-button size="small" type="link" @click="copyField(String(created.port), '端口')">复制</a-button>
        </div>

        <a-divider style="margin:8px 0" />

        <div class="cred-row">
          <span class="cred-label">账号</span>
          <span class="cred-value">{{ created.username }}</span>
          <a-button size="small" type="link" @click="copyField(created.username, '账号')">复制</a-button>
        </div>

        <a-divider style="margin:8px 0" />

        <div class="cred-row">
          <span class="cred-label">密码</span>
          <span class="cred-value">{{ created.password }}</span>
          <a-button size="small" type="link" @click="copyField(created.password, '密码')">复制</a-button>
        </div>

        <a-divider style="margin:8px 0" />

        <div class="cred-row">
          <span class="cred-label">到期</span>
          <span class="cred-value">{{ formatExpire(created.expire_at) }}</span>
          <a-button size="small" type="link" @click="copyField(formatExpire(created.expire_at), '到期')">复制</a-button>
        </div>
      </div>

      <a-divider style="margin:16px 0" />

      <a-space style="width:100%" :style="{ display: 'flex', justifyContent: 'space-between' }">
        <a-button @click="copyAll">复制 5 字段</a-button>
        <a-space>
          <a-button @click="onAnother">再开一个</a-button>
          <a-button type="primary" @click="onClose">关闭</a-button>
        </a-space>
      </a-space>
    </div>
    <BatchHealthcheckModal v-model:visible="healthVisible" :proxy-ids="createdProxyIds" />
  </a-modal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { message } from 'ant-design-vue';
import { createProxy } from '../api/proxies';
import { getNode, getNodePortSuggestion, resolveNodeAddress } from '../api/nodes';
import { getDashboard } from '../api/dashboard';
import ProxyUserPicker from './ProxyUserPicker.vue';
import BatchHealthcheckModal from './BatchHealthcheckModal.vue';
import { buildCreateProxyBody, buildProxyCopyDraft } from './proxyCopyCreate';
import { classifyCreateFailure } from './batchCreateFailure';
import type { Region, NodeSummary, ProxyItem } from '../types';

type Stage = 'form' | 'done' | 'batch-done';
type Created = {
  proxy_id: number;
  node_id: number;
  node_name: string;
  port: number;
  username: string;
  password: string;
  expire_at: string;
  ip_public: string;
  copy_string: string;
  region_ids: number[];
  region_names: string[];
};

const props = defineProps<{
  visible: boolean;
  region?: any | null;
  copySource?: ProxyItem | null;
}>();
const emit = defineEmits<{ (e: 'update:visible', v: boolean): void; (e: 'created', r: any): void }>();

const stage = ref<Stage>('form');
const batchMode = ref(false);
const isBatchMode = computed(() => batchMode.value && !isCopyMode.value);
const selectedNodeIds = ref<number[]>([]);
const batchCreated = ref<Created[]>([]);
const batchFailures = ref<{ nodeID: number; nodeName: string; reason: string; retryable: boolean }[]>([]);
const retryNodeIds = computed(() => batchFailures.value.filter(f => f.retryable).map(f => f.nodeID));
const batchProgress = ref(0);
const healthVisible = ref(false);
// Only proxies the server confirmed are worth probing; failures and
// unconfirmed results have nothing (or something unknown) to check.
const createdProxyIds = computed(() => batchCreated.value.map(item => item.proxy_id));
const openHealthcheck = () => {
  if (!createdProxyIds.value.length || loading.value || batchCopying.value) return;
  healthVisible.value = true;
};
const batchCopying = ref(false);
let batchBody: import('../api/proxies').CreateProxyBody | null = null;
const duration = ref('1m');
const expireAt = ref('');
const customN = ref<number>(7);   // default 7 days — a sensible trial length
const unit = ref<'d' | 'm'>('d'); // default to days
const port = ref<number | null>(null);
const portSuggestionLoading = ref(false);
const portIsSuggested = ref(false);
const portUserEdited = ref(false);
let portSuggestionReqSeq = 0;
let applyingSuggestedPort = false;
const note = ref('');
const username = ref('');
const password = ref('');
const loading = ref(false);
const created = ref<Created | null>(null);
const selectedProxyUserID = ref<number | null>(null);

// Multi-region: 1..N regions per proxy. First element is the primary.
const selectedRegionIds = ref<number[]>([]);
const regions = ref<Region[]>([]);
const regionsLoading = ref(false);

// Node picker: null = "auto" (let backend alloc pick). When the
// operator has a specific NAT box in mind, they pin the proxy to it.
const selectedNodeId = ref<number | null>(null);
const nodes = ref<NodeSummary[]>([]);
const nodesLoading = ref(false);
const selectedNodeExpireAt = ref<string | null>(null);
let nodeExpireReqSeq = 0;

// When parent supplies a single region prop (Dashboard flow), prefill
// the selection with just that region.
watch(() => props.region, (r) => {
  if (r) {
    selectedRegionIds.value = [r.region_id];
  } else {
    selectedRegionIds.value = [];
  }
}, { immediate: true });

// Effective single-region view: the first selected region, for places
// in the UI that just need to display "the" region.
const effectiveRegion = computed<any | null>(() => {
  if (props.region) return props.region;
  if (selectedRegionIds.value.length === 0) return null;
  const first = selectedRegionIds.value[0];
  return regions.value.find((r) => r.region_id === first) || null;
});

const regionOptions = computed(() =>
  regions.value.map((r) => ({
    label: `${r.region_name} (已用 ${r.used}/${r.capacity})`,
    value: r.region_id,
  })),
);

const filterRegion = (input: string, option: any) =>
  option.label.toLowerCase().includes(input.toLowerCase());

// Node options must filter out nodes already serving ANY of the
// selected regions (multi-region: union of occupied sets).
const nodeOptions = computed(() => {
  if (selectedRegionIds.value.length === 0) return [];
  const occupied = new Set<number>();
  for (const r of regions.value) {
    if (selectedRegionIds.value.includes(r.region_id)) {
      for (const id of r.node_ids || []) occupied.add(id);
    }
  }
  const candidates = nodes.value.filter(
    (n) =>
      n.status === 'online' &&
      n.used_ports < n.capacity_ports &&
      !occupied.has(n.id),
  );
  return candidates.map((n) => ({
    label: `${n.name} (所选大区均可用 · 节点总 ${n.used_ports}/${n.capacity_ports})`,
    value: n.id,
  }));
});

const filterNode = (input: string, option: any) =>
  String(option.label).toLowerCase().includes(input.toLowerCase());

const isCopyMode = computed(() => props.copySource != null);

const submitDisabled = computed(() =>
  !effectiveRegion.value ||
  selectedProxyUserID.value === null ||
  loading.value ||
  (isBatchMode.value && selectedNodeIds.value.length === 0) ||
  (isCopyMode.value && selectedNodeId.value === null),
);

const assignSuggestedPort = (value: number | null) => {
  applyingSuggestedPort = true;
  port.value = value;
  applyingSuggestedPort = false;
};

const onPortChange = (value: number | null) => {
  if (applyingSuggestedPort) return;
  portUserEdited.value = value !== null && value !== undefined;
  portIsSuggested.value = false;
};

const loadPortSuggestion = async (nodeId: number) => {
  portSuggestionReqSeq += 1;
  const reqSeq = portSuggestionReqSeq;
  portSuggestionLoading.value = true;
  try {
    const res = await getNodePortSuggestion(nodeId);
    if (reqSeq !== portSuggestionReqSeq || selectedNodeId.value !== nodeId) return;
    if (!portUserEdited.value && res.data.port != null) {
      portIsSuggested.value = true;
      assignSuggestedPort(res.data.port);
    }
  } catch {
    if (reqSeq === portSuggestionReqSeq && selectedNodeId.value === nodeId) {
      message.warning('端口建议加载失败，留空仍可自动分配');
    }
  } finally {
    if (reqSeq === portSuggestionReqSeq) {
      portSuggestionLoading.value = false;
    }
  }
};

// Reset selection when regions change.
watch(selectedRegionIds, () => {
  selectedNodeIds.value = [];
  selectedNodeId.value = null;
  selectedNodeExpireAt.value = null;
});

watch(selectedNodeId, async (id) => {
  nodeExpireReqSeq += 1;
  const reqSeq = nodeExpireReqSeq;
  selectedNodeExpireAt.value = null;

  portSuggestionReqSeq += 1;
  portSuggestionLoading.value = false;
  if (portIsSuggested.value) {
    assignSuggestedPort(null);
    portIsSuggested.value = false;
    portUserEdited.value = false;
  }
  if (id == null) return;

  void loadPortSuggestion(id);
  try {
    const r = await getNode(id);
    if (reqSeq !== nodeExpireReqSeq || selectedNodeId.value !== id) return;
    selectedNodeExpireAt.value = r.data?.expire_at || null;
  } catch {
    if (reqSeq !== nodeExpireReqSeq || selectedNodeId.value !== id) return;
    selectedNodeExpireAt.value = null;
  }
});

// Clamp customN to the new unit's range so flipping 天↔月 doesn't
// leave the input holding an out-of-range value (e.g. user typed 500,
// switched to 月 — pin it to 24 silently). The :max prop on the input
// will physically prevent typing >24 in 月 mode, but this also handles
// the case where unit changes via arrow buttons / programmatic set.
watch(unit, (newUnit) => {
  if (newUnit === 'm' && customN.value > 24) customN.value = 24;
  if (newUnit === 'd' && customN.value > 730) customN.value = 730;
  recalc();
});
watch(customN, () => recalc());

const title = computed(() => {
  if (stage.value === 'batch-done') return '批量创建结果';
  if (isBatchMode.value) return '批量创建 proxy';
  if (stage.value === 'done') return `节点 #${created.value?.proxy_id ?? ''} · ${effectiveRegion.value?.region_name ?? ''}`;
  if (isCopyMode.value) return `复制创建节点 · 来源 #${props.copySource?.proxy_id ?? ''}`;
  const regionName = effectiveRegion.value?.region_name;
  return regionName ? `新建节点 · ${regionName}` : '新建节点';
});

const nodeExpireConflict = computed(() => {
  if (selectedNodeId.value == null) return false;
  if (!selectedNodeExpireAt.value) return false;
  const nodeT = new Date(selectedNodeExpireAt.value).getTime();
  const proxyT = new Date(`${expireAt.value}T00:00:00`).getTime();
  if (Number.isNaN(nodeT) || Number.isNaN(proxyT)) return false;
  return proxyT > nodeT;
});

const nodeExpireLabel = computed(() => {
  if (!selectedNodeExpireAt.value) return '-';
  const t = new Date(selectedNodeExpireAt.value);
  if (Number.isNaN(t.getTime())) return selectedNodeExpireAt.value;
  const y = t.getFullYear();
  const m = String(t.getMonth() + 1).padStart(2, '0');
  const d = String(t.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
});

const recalc = () => {
  const d = new Date();
  if (duration.value === 'custom') {
    // Clamp negative / zero input so the preview never shows a past date.
    const n = Math.max(1, Number(customN.value) || 1);
    if (unit.value === 'd') {
      d.setDate(d.getDate() + n);
    } else {
      d.setMonth(d.getMonth() + n);
    }
  } else {
    const m = { '1m': 1, '3m': 3, '6m': 6, '12m': 12 }[duration.value] || 1;
    d.setMonth(d.getMonth() + m);
  }
  expireAt.value = d.toISOString().slice(0, 10);
};

const loadRegions = async () => {
  regionsLoading.value = true;
  nodesLoading.value = true;
  try {
    // /api/regions returns RegionConfig (id/name/domains/ips) — but the
    // select dropdown needs Region (region_id/region_name + capacity
    // info for the "已用 X/Y" label). /api/dashboard/regions returns
    // the rich Region shape and is already used by the Dashboard view
    // for the same data, so reuse it here to keep both views in sync.
    // The same response carries node summaries, which we need for the
    // node picker below.
    const r = await getDashboard();
    regions.value = r.data?.regions || [];
    nodes.value = r.data?.nodes || [];
  } catch {
    regions.value = [];
    nodes.value = [];
  } finally {
    regionsLoading.value = false;
    nodesLoading.value = false;
  }
};

const resetForm = () => {
  stage.value = 'form';
  healthVisible.value = false;
  selectedNodeIds.value = [];
  batchCreated.value = [];
  batchFailures.value = [];
  batchBody = null;
  batchProgress.value = 0;
  duration.value = '1m';
  customN.value = 7;     // sensible trial-default
  unit.value = 'd';
  port.value = null;
  portSuggestionLoading.value = false;
  portIsSuggested.value = false;
  portUserEdited.value = false;
  portSuggestionReqSeq += 1;
  applyingSuggestedPort = false;
  note.value = '';
  username.value = '';
  password.value = '';
  selectedProxyUserID.value = null;
  selectedRegionIds.value = props.region ? [props.region.region_id] : [];
  selectedNodeId.value = null;
  selectedNodeExpireAt.value = null;
  loading.value = false;
  created.value = null;
  recalc();
};

const initializeForm = () => {
  resetForm();
  if (!props.copySource) return;
  const draft = buildProxyCopyDraft(props.copySource);
  selectedProxyUserID.value = draft.proxyUserID;
  selectedRegionIds.value = draft.regionIds;
  selectedNodeId.value = draft.nodeId;
  port.value = draft.port;
  portUserEdited.value = draft.port !== null;
  note.value = draft.note;
  expireAt.value = draft.expireDate;
  username.value = draft.username;
  password.value = draft.password;
};

watch(() => props.visible, (v) => {
  if (v) {
    initializeForm();
    loadRegions();
  }
});

const onSubmit = async () => {
  if (loading.value) return;
  if (selectedProxyUserID.value === null) {
    message.error('请先选择所属用户');
    return;
  }
  if (selectedRegionIds.value.length === 0) {
    message.error('请先选择大区');
    return;
  }
  if (isCopyMode.value && selectedNodeId.value === null) {
    message.error('请选择目标节点');
    return;
  }
  if (isCopyMode.value && (!/^[0-9]{6}$/.test(username.value) || !/^[0-9]{6}$/.test(password.value))) {
    message.error('账号和密码必须分别为 6 位数字');
    return;
  }
  if (isCopyMode.value) {
    const expireTimestamp = Date.parse(`${expireAt.value}T23:59:59+08:00`);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(expireAt.value) || Number.isNaN(expireTimestamp) || expireTimestamp <= Date.now()) {
      message.error('请选择未来的有效期日期');
      return;
    }
  }
  if (!isBatchMode.value && nodeExpireConflict.value) {
    message.error('所选服务器到期时间早于节点到期，请更换服务器或缩短有效期');
    return;
  }
  if (isBatchMode.value) {
    if (!selectedNodeIds.value.length) return;
    batchBody = {
      proxy_user_id: selectedProxyUserID.value,
      region_ids: [...selectedRegionIds.value],
      duration: duration.value === 'custom' ? `${customN.value}${unit.value}` : duration.value,
      ...(note.value.trim() ? { note: note.value.trim() } : {}),
    };
    await submitBatch([...selectedNodeIds.value]);
    return;
  }
  loading.value = true;
  try {
    const body = buildCreateProxyBody({
      mode: isCopyMode.value ? 'copy' : 'create',
      proxyUserID: selectedProxyUserID.value,
      regionIds: selectedRegionIds.value,
      duration:
        duration.value === 'custom'
          ? `${customN.value}${unit.value}`
          : duration.value,
      expireDate: expireAt.value,
      nodeId: selectedNodeId.value,
      port: port.value,
      note: note.value,
      username: username.value,
      password: password.value,
    });
    const res = await createProxy(body);
    const c: Created = res.data;
    created.value = c;
    stage.value = 'done';
    try {
      await navigator.clipboard.writeText(await buildResolvedCopyString(c));
    } catch (e: any) {
      message.warning(e.response?.data?.error?.message || '创建成功，但节点域名解析失败，未自动复制');
    }
    emit('created', c);
  } catch (e: any) {
    const code = e.response?.data?.error?.code;
    if (code === 'PORT_IN_USE' && selectedNodeId.value != null) {
      message.error('候选端口已被占用，已刷新建议');
      assignSuggestedPort(null);
      portIsSuggested.value = false;
      portUserEdited.value = false;
      void loadPortSuggestion(selectedNodeId.value);
    } else if (code === 'USERNAME_IN_USE') {
      message.error('账号已存在，请修改后重试');
    } else {
      message.error(e.response?.data?.error?.message || '生成失败');
    }
  } finally {
    loading.value = false;
  }
};

// Reuse server-side allocation and validation; never send a shared manual port.
const submitBatch = async (nodeIDs: number[]) => {
  if (loading.value || !batchBody) return;
  loading.value = true;
  batchProgress.value = 0;
  batchFailures.value = batchFailures.value.filter(f => !nodeIDs.includes(f.nodeID));
  try {
    for (const nodeID of nodeIDs) {
      try {
        const { data } = await createProxy({ ...batchBody, node_id: nodeID });
        const item: Created = data;
        item.copy_string = item.ip_public
          ? [item.ip_public, item.port, item.username, item.password, formatExpire(item.expire_at)].join('|')
          : '';
        batchCreated.value.push(item);
      } catch (e: any) {
        const failure = classifyCreateFailure(e);
        batchFailures.value.push({
          nodeID,
          nodeName: nodes.value.find(n => n.id === nodeID)?.name || `服务器 #${nodeID}`,
          reason: failure.message,
          retryable: failure.kind === 'retryable',
        });
      } finally {
        batchProgress.value += 1;
      }
    }
    stage.value = 'batch-done';
    emit('created', batchCreated.value);
  } finally {
    loading.value = false;
  }
};

const copyBatch = async () => {
  if (batchCopying.value || !batchCreated.value.length) return;
  batchCopying.value = true;
  try {
    const lines: string[] = [];
    for (const item of batchCreated.value) {
      lines.push(await buildResolvedCopyString(item));
    }
    await navigator.clipboard.writeText(lines.join('\n'));
    message.success('全部配置已复制');
  } catch (e: any) {
    message.error(e.response?.data?.error?.message || '地址解析或复制失败，请重试一键复制');
  } finally {
    batchCopying.value = false;
  }
};

const copyField = async (text: string, label: string) => {
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
    message.success(`${label}已复制`);
  } catch {
    message.error('复制失败');
  }
};

const copyAll = async () => {
  if (!created.value) return;
  try {
    await copyField(await buildResolvedCopyString(created.value), '全部');
  } catch (e: any) {
    message.error(e.response?.data?.error?.message || '节点域名解析失败，未复制配置');
  }
};

const buildResolvedCopyString = async (createdProxy: Created) => {
  const resolved = await resolveNodeAddress(createdProxy.node_id);
  createdProxy.ip_public = resolved.data.resolved_ip;
  const joined = [
    createdProxy.ip_public,
    String(createdProxy.port),
    createdProxy.username,
    createdProxy.password,
    formatExpire(createdProxy.expire_at),
  ].join('|');
  createdProxy.copy_string = joined;
  return joined;
};

const onAnother = () => {
  // Stay open, reset the form so the user can immediately create a
  // second proxy with the same defaults.
  initializeForm();
  loadRegions();
};

const onClose = () => {
  if (loading.value || batchCopying.value) return;
  healthVisible.value = false;
  emit('update:visible', false);
};

const onCancel = () => {
  if (loading.value || batchCopying.value) return;
  // X button acts the same as the close button: just close. We do
  // NOT reset state here because the user might reopen immediately.
  emit('update:visible', false);
};

// "2026-09-24T17:33:08+08:00" → "2026-09-24" for display; matches the
// one-line copy format.
const formatExpire = (s: string) => s?.slice(0, 10) ?? '';
</script>

<style scoped>
.batch-preview { margin-top: 12px; padding: 12px; border: 1px solid var(--app-border-color, #e5e7eb); border-radius: 6px; }
.batch-config { margin-top: 6px; overflow-wrap: anywhere; font-family: monospace; user-select: all; }
.cred-block {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 12px 16px;
}
.cred-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.cred-label {
  width: 48px;
  color: #6b7280;
  font-size: 13px;
  flex-shrink: 0;
}
.cred-value {
  flex: 1;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 14px;
  color: #111827;
  word-break: break-all;
}
</style>
